import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
export var JournalEntryType;
(function (JournalEntryType) {
    JournalEntryType["Text"] = "Text";
    JournalEntryType["Audio"] = "Audio";
    JournalEntryType["Attachment"] = "Attachment";
    JournalEntryType["Photo"] = "Photo";
    JournalEntryType["Emotion"] = "Emotion";
})(JournalEntryType || (JournalEntryType = {}));
export const journalEntrySchema = z.object({
    id: z.string().optional(),
    journalId: z.string(),
    type: z.nativeEnum(JournalEntryType),
    value: z.string(),
    caption: z.string().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const createJournalEntryRequestSchema = z.object({
    journalId: z.string().optional(),
    type: z.nativeEnum(JournalEntryType).default(JournalEntryType.Text),
    value: z.string().min(1, 'Value is required'),
    caption: z.string().optional().default(''),
});
const createJournalEntryResponseSchema = z.object({
    id: z.string(),
    journalId: z.string(),
    type: z.nativeEnum(JournalEntryType),
    value: z.string(),
    caption: z.string().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
const createJournalPhotoEntryRequestSchema = z
    .object({
    journalId: z.string(),
    value: z.instanceof(File),
    caption: z.string().optional().default(''),
})
    .refine((data) => data.value.type.startsWith('image/'), {
    message: 'File must be an image',
});
export const useCreateJournalEntry = (request) => useMutation({
    mutationFn: (data) => {
        const validatedData = createJournalEntryRequestSchema.parse(data);
        return request({
            url: `/journals/${validatedData.journalId}/entries`,
            method: 'POST',
            data: validatedData,
        }, createJournalEntryResponseSchema);
    },
});
export const useCreateJournalPhotoEntry = (request) => useMutation({
    mutationFn: (data) => {
        const validatedData = createJournalPhotoEntryRequestSchema.parse(data);
        const formData = new FormData();
        formData.append('photo', validatedData.value);
        formData.append('caption', validatedData.caption || '');
        return request({
            url: `/journals/${validatedData.journalId}/upload-photo`,
            method: 'POST',
            headers: {
                'Content-Type': 'multipart/form-data',
                Accept: '*/*',
            },
            data: formData,
        }, createJournalEntryResponseSchema);
    },
});
export const updateJournalEntryRequestSchema = z.object({
    value: z.string().min(1, 'Value cannot be empty').optional(),
    caption: z.string().optional(),
});
export const useUpdateJournalEntry = (request) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            const validatedJournalId = z.string().min(1).parse(data.journalId);
            const validatedEntryId = z.string().min(1).parse(data.entryId);
            const validatedData = updateJournalEntryRequestSchema.parse({
                value: data.value,
                caption: data.caption,
            });
            return request({
                url: `/journals/${validatedJournalId}/entries/${validatedEntryId}`,
                method: 'PUT',
                data: validatedData,
            });
        },
        onSuccess: (_response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['journal', variables.journalId] });
            queryClient.invalidateQueries({ queryKey: ['journal'] });
        },
    });
};
export const useDeleteJournalEntry = (request) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            const validatedJournalId = z.string().min(1).parse(data.journalId);
            const validatedEntryId = z.string().min(1).parse(data.entryId);
            return request({
                url: `/journals/${validatedJournalId}/entries/${validatedEntryId}`,
                method: 'DELETE',
            });
        },
        onSuccess: (_response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['journal', variables.journalId] });
            queryClient.invalidateQueries({ queryKey: ['journal'] });
        },
    });
};
